"use client";

import { useEffect, useRef, useState, type CSSProperties } from "react";
import { motion, useReducedMotion } from "framer-motion";
import type { Translations } from "@/lib/i18n";

export type VisualLabels = Translations["showcase"]["visuals"];

export interface VisualProps {
  active: boolean;
  v: VisualLabels;
}

export const GLASS: CSSProperties = {
  background: "rgba(16,16,26,0.82)",
  border: "1px solid rgba(255,255,255,0.09)",
  backdropFilter: "blur(14px)",
  WebkitBackdropFilter: "blur(14px)",
  boxShadow: "0 18px 50px -18px rgba(0,0,0,0.85)",
};

export const SPRING = { type: "spring", stiffness: 380, damping: 30 } as const;

/**
 * Types `text` out while `active`; shows it in full for reduced-motion users.
 * Changing `cycle` restarts the typing from the first character.
 */
export function useTypewriter(text: string, active: boolean, speed = 70, cycle = 0) {
  const reduceMotion = useReducedMotion();
  const [state, setState] = useState({ n: 0, cycle });
  const n = state.cycle === cycle ? state.n : 0;
  useEffect(() => {
    if (!active || n >= text.length) return;
    const id = setTimeout(() => setState({ n: n + 1, cycle }), speed);
    return () => clearTimeout(id);
  }, [active, n, text.length, speed, cycle]);
  return reduceMotion ? text : text.slice(0, n);
}

export function Caret({ color = "#fff" }: { color?: string }) {
  return (
    <motion.span
      className="ml-px inline-block h-[1.05em] w-[1.5px] align-middle"
      style={{ background: color }}
      animate={{ opacity: [1, 0, 1] }}
      transition={{ duration: 1, repeat: Infinity }}
    />
  );
}

/** Muted looping clip that only downloads/plays while `active`. */
export function LoopVideo({
  src,
  poster,
  active,
  className = "",
}: {
  src: string;
  poster: string;
  active: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (active) el.play().catch(() => {});
    else el.pause();
  }, [active]);
  return (
    <video
      ref={ref}
      src={src}
      poster={poster}
      muted
      loop
      playsInline
      preload="none"
      aria-hidden
      className={className}
      // Video browser extensions inject classes before hydration
      suppressHydrationWarning
    />
  );
}

export function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia(query);
    setMatches(mql.matches);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}
